import { useEffect, useState, useCallback } from "react";
import { useForm, Controller } from 'react-hook-form';
import ProductService from "../../services/ProductService";
import { toast, ToastContainer } from "react-toastify";
import AppHeader from "../../components/AppHeader";
import AppSidebar from "../../components/AppSidebar";
import PageHeader from "../../components/PageHeader";
import CategoryService from "../../services/CategoryService";
import AsyncSelect from 'react-select/async';
import { useParams } from "react-router-dom";
import UploadFile from "../../components/UploadFile";
import TagsInput from "../../components/TagsInput";
import debounce from 'lodash.debounce';
import SelectTag from "../../components/SelectTag";

const EditProduct = () => {
    const [loading, setLoading] = useState(false);
    const { register, handleSubmit, control, setValue, watch, formState: { errors }, reset, getValues } = useForm({
        defaultValues: {
            in_stock: true,
        },
    });
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [uploadedUrls, setUploadedUrls] = useState([]);
    const [tags, setTags] = useState([]);
    const [skuAvailable, setSkuAvailable] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const sku = watch("sku");
    const { id } = useParams();
    const selectedProductType = watch("type") || "goods";
    const gstSearchUrl = "https://services.gst.gov.in/services/searchhsnsac";

    const productTypeOptions = [
        { value: 'goods', label: 'Goods' },
        { value: 'service', label: 'Service' }
    ]

    const statusOptions = [
        { value: 'drafted', label: 'Drafted' },
        { value: 'published', label: 'Published' }
    ]

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const validateSku = useCallback(
        debounce(async (sku) => {
            if (!sku) {
                setSkuAvailable(false);
                return false;
            }
            setIsChecking(true);
            try {
                const response = await ProductService.checkUniqueSKU(sku);
                if (response?.data?.success) {
                    setValue('sku', response?.data?.data);
                    setSkuAvailable(true);
                } else {
                    setSkuAvailable(false);
                }
            } catch (error) {
                console.error("Error in checking SKU:", error);
                setSkuAvailable(false);
            } finally {
                setIsChecking(false);
            }
        }, 1000),
        [setIsChecking, setSkuAvailable]);

    const formattedTags = (tags) => {
        if (typeof tags === "string") {
            try {
                tags = JSON.parse(tags);
            } catch (error) {
                console.error("Error parsing tags:", error);
                tags = [];
            }
        }

        const timestamp = new Date().getTime();
        return tags?.map((tag, index) => ({
            id: `${timestamp}-${index}`,
            text: tag,
        })) || [];
    };

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await ProductService.get(id);
                if (response.status === 200) {
                    const fetchedProduct = response?.data?.data;
                    const tags = formattedTags(fetchedProduct?.tags);
                    setTags(tags);
                    const categoryId = fetchedProduct?.category_id;
                    const categoryOption = categoryId ? {
                        value: fetchedProduct?.category?.id,
                        label: fetchedProduct?.category?.name
                    } : null;
                    reset(fetchedProduct);
                    setSelectedCategory(categoryOption);
                    setValue("category_id", categoryOption ? categoryOption.value : null);

                } else {
                    toast.error(response?.data?.message || 'Error fetching product');
                }
            } catch (error) {
                toast.error(error?.message || "An error occurred!");
            }
        };
        fetchProduct();
    }, [id, reset, setValue]);

    const categoryOptions = async () => {
        try {
            const categoryList = await CategoryService.listAll();
            if (categoryList && Array.isArray(categoryList.data.data)) {
                return categoryList.data.data.map(category => ({
                    value: category.id,
                    label: category.name
                }));
            } else {
                console.error('Expected an array in categoryList.data.data, but got:', categoryList.data);
                return [];
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            return [];
        }
    }
    const handleFileUpload = (urls) => {
        setUploadedUrls(urls);
    };

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            data.image = uploadedUrls ?? '';
            const tagsText = tags.map(tag => tag.text);
            const response = await ProductService.update(id, { data: { ...data, tags: tagsText } });
            if (response.status === 200) {
                toast.success(response?.data?.message);
                setSkuAvailable(false);
                const responseData = response?.data?.data;
                reset(responseData);
                const categoryOption = {
                    value: responseData?.category?.id,
                    label: responseData?.category?.name
                };
                setValue("category_id", categoryOption.value);
                setSelectedCategory(categoryOption);
            } else {
                console.error("Error in updating product:", response);
                toast.error(response?.data?.message);
            }
        } catch (error) {
            console.error("Error updating product:", error);
            toast.error(error?.message || "An error occurred!");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <AppSidebar />
            <div className="main-content">
                <AppHeader />
                <div className="container-fluid">
                    <PageHeader title='Edit Product' button_label1="Back" link_to1="/products" className1="btn btn-sm btn-primary" />
                    <div className="row mt-5">
                        <div className="col-12 col-xl-8">
                            <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-12 col-md-12 mb-3">
                                                <div className="form-group">
                                                    <label htmlFor="name">Name</label>
                                                    <input type="text" {...register("name", { required: true })} className="form-control" placeholder="Enter product name" onBlur={(e) => {
                                                        if (sku !== getValues("sku")) {
                                                            setValue("sku", e.target.value.toLowerCase().replace(/\s+/g, '-'));
                                                        }
                                                    }} />
                                                    {errors.name && <span className="text-danger">Name is required</span>}
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 mb-3">
                                                <div className="form-group">
                                                    <label htmlFor="quantity">Quantity</label>
                                                    <input type="number" {...register("quantity", { required: true })} className="form-control" placeholder="Enter product quantity" />
                                                    {errors.quantity && <span className="text-danger">Quantity is required</span>}
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 mb-3">
                                                <div className="form-group">
                                                    <label htmlFor="sku">SKU</label>
                                                    <input type="text" {...register("sku", { required: true })} className="form-control" placeholder="Enter product SKU" onChange={(e) => validateSku(e.target.value)} />
                                                    {errors.sku && <span className="text-danger">SKU is required</span>}
                                                    {isChecking && <div className="text-info">Checking SKU...</div>}
                                                    {skuAvailable && (<div className="text-success">Available</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 mb-3">
                                                {selectedProductType === 'goods' && (
                                                    <div className="form-group">
                                                        <label htmlFor="identification_code">HSN Code</label>
                                                        <input
                                                            type="text"
                                                            {...register("identification_code", { required: true })}
                                                            className="form-control"
                                                            placeholder="Enter HSN code"
                                                        />
                                                        {errors.identification_code && <span className="text-danger">HSN Code is required</span>}
                                                    </div>
                                                )}

                                                {selectedProductType === 'service' && (
                                                    <div className="form-group">
                                                        <label htmlFor="identification_code">SAC Code</label>
                                                        <input
                                                            type="text"
                                                            {...register("identification_code", { required: true })}
                                                            className="form-control"
                                                            placeholder="Enter SAC code"
                                                        />
                                                        {errors.identification_code && <span className="text-danger">SAC Code is required</span>}
                                                    </div>
                                                )}
                                                <div className="mt-1 d-flex" style={{ fontSize: '12px' }}>
                                                    <span className="me-2">Check here: </span> <a href={gstSearchUrl} target="_blank" rel="noopener noreferrer">
                                                        {gstSearchUrl}
                                                    </a>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 mb-3">
                                                <div className="form-group">
                                                    <label htmlFor="weight">Weight</label>
                                                    <input type="text" {...register("weight")} className="form-control" placeholder="Enter product weight" maxLength={10} />
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-12 mb-3">
                                                <div className="form-group">
                                                    <label htmlFor="tags">Tags</label>
                                                    <TagsInput tags={tags} setTags={setTags} />
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-12 mb-3">
                                                <div className="form-group">
                                                    <label htmlFor="description">Description</label>
                                                    <textarea className="form-control" {...register("description")} rows={4} placeholder="Enter product description"></textarea>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card">
                                    <div className="card-body">
                                        <div className="form-group">
                                            <label className="mb-1">
                                                Image
                                            </label>
                                            <small className="form-text text-body-secondary">
                                                Upload any files you want to start the product with.
                                            </small>
                                            <div className="card">
                                                <div className="card-body">
                                                    <UploadFile folderName="products" onFileUpload={handleFileUpload} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                                {loading ? 'Saving...' : 'Save'}
                                            </button>
                                            {loading && (
                                                <div className="mt-3">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="sr-only">Loading...</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="col-12 col-xl-4">
                            <div className="card">
                                <div className="card-header">
                                    <h4 className="card-header-title">
                                        Organize
                                    </h4>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-12 col-md-12 mb-3">
                                            <div className="form-group">
                                                <div className="form-group">
                                                    <label htmlFor="type">Product Type</label>
                                                    <SelectTag
                                                        name="type"
                                                        control={control}
                                                        options={productTypeOptions}
                                                        label="type"
                                                        defaultValue={productTypeOptions[1].value}
                                                    />
                                                    {errors.type && <span className="text-danger">{errors.type?.message}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-12 col-md-12 mb-3">
                                            <div className="form-group">
                                                <label htmlFor="status">Status</label>
                                                <SelectTag
                                                    name="status"
                                                    control={control}
                                                    options={statusOptions}
                                                    label="Status"
                                                    defaultValue={statusOptions[1].value}
                                                />
                                                {errors.status && <span className="text-danger">{errors.status?.message}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mt-3">
                                        <div className="col-12 col-md-12 mb-3">
                                            <div className="form-group">
                                                <label htmlFor="category_id">Category</label>
                                                <Controller
                                                    name="category_id"
                                                    control={control}
                                                    rules={{ required: "Category is required" }}
                                                    render={({ field }) => (
                                                        <AsyncSelect
                                                            {...field}
                                                            cacheOptions
                                                            defaultOptions
                                                            loadOptions={categoryOptions}
                                                            onChange={(selectedOption) => {
                                                                setSelectedCategory(selectedOption);
                                                                setValue("category_id", selectedOption?.value || null);
                                                                field.onChange(selectedOption ? selectedOption.value : null);
                                                            }}
                                                            value={selectedCategory ?? null}
                                                            placeholder="Select a category" />
                                                    )}
                                                />
                                                {errors.category_id && <span className="text-danger">Category is required</span>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="card">
                                <div className="card-header">
                                    <h4 className="card-header-title">
                                        Pricing
                                    </h4>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-12 col-md-12 mb-3">
                                            <div className="form-group">
                                                <label htmlFor="display_price">Display Price</label>
                                                <input type="text" {...register("display_price", { required: true })} className="form-control" min={0} placeholder="Enter display price" />
                                                {errors.display_price && <span className="text-danger">Display price is required</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-12 col-md-12 mb-3">
                                            <div className="form-group">
                                                <label htmlFor="selling_price">Selling Price</label>
                                                <input type="text" {...register("selling_price", { required: true })} className="form-control" min={0} placeholder="Enter selling price" />
                                                {errors.selling_price && <span className="text-danger">Selling price is required</span>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <div className="d-flex justify-content-between align-items-center pt-2">
                                        <span className="mb-0">In stock</span>
                                        <div className="w-25 d-flex justify-content-end">
                                            <div className="form-check form-switch me-n3">
                                                <input type="checkbox" className="form-check-input"  {...register("in_stock")} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer />
        </>
    );
};

export default EditProduct;
